
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import BuyerLayout from "@/components/layouts/BuyerLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText, Clock, CheckCircle, XCircle, ArrowRight, Search } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

// Mock data for orders
const mockOrders = [
  {
    id: "ORD-001",
    associationName: "Sunset Condominiums",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2), // 2 days ago
    status: "approved",
    documentCount: 3,
    totalAmount: 154.99,
  },
  {
    id: "ORD-002",
    associationName: "Highland Heights HOA",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 12), // 12 hours ago
    status: "under_review",
    documentCount: 2,
    totalAmount: 89.95,
  },
  {
    id: "ORD-003",
    associationName: "Parkview Residences",
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5), // 5 days ago
    status: "denied",
    documentCount: 1,
    totalAmount: 45.00,
  },
];

const Dashboard = () => {
  const { user } = useAuth();
  const [recentOrders, setRecentOrders] = useState(mockOrders);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate loading data
    const timer = setTimeout(() => {
      setLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return <Badge className="bg-green-500">Approved</Badge>;
      case "under_review":
        return <Badge className="bg-blue-500">Under Review</Badge>;
      case "denied":
        return <Badge className="bg-red-500">Denied</Badge>;
      case "pending_payment":
        return <Badge className="bg-yellow-500">Pending Payment</Badge>;
      default:
        return <Badge className="bg-gray-500">In Progress</Badge>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case "under_review":
        return <Clock className="h-8 w-8 text-blue-500" />;
      case "denied":
        return <XCircle className="h-8 w-8 text-red-500" />;
      default:
        return <FileText className="h-8 w-8 text-gray-500" />;
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    }).format(date);
  };

  return (
    <BuyerLayout>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-gray-500">
              Welcome back, {user?.firstName}!
            </p>
          </div>
          <Button asChild>
            <Link to="/buyer/search">
              <Search className="mr-2 h-4 w-4" />
              Request Documents
            </Link>
          </Button>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card>
            <CardHeader className="space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{recentOrders.length}</div>
              <p className="text-xs text-muted-foreground">
                +0% from last month
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {recentOrders.filter(order => order.status === "under_review").length}
              </div>
              <p className="text-xs text-muted-foreground">
                Orders awaiting review
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Spent</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                ${recentOrders.reduce((sum, order) => sum + order.totalAmount, 0).toFixed(2)}
              </div>
              <p className="text-xs text-muted-foreground">
                For all document orders
              </p>
            </CardContent>
          </Card>
        </div>

        <div>
          <Card>
            <CardHeader>
              <CardTitle>Recent Orders</CardTitle>
              <CardDescription>
                View and track your recent document requests
              </CardDescription>
            </CardHeader>
            <CardContent>
              {recentOrders.length > 0 ? (
                <div className="space-y-4">
                  {recentOrders.map((order) => (
                    <div
                      key={order.id}
                      className="flex flex-col sm:flex-row items-start sm:items-center justify-between border rounded-lg p-4 gap-3"
                    >
                      <div className="flex items-start space-x-4">
                        <div>
                          {getStatusIcon(order.status)}
                        </div>
                        <div>
                          <div className="font-medium">{order.associationName}</div>
                          <div className="text-sm text-muted-foreground">
                            Order #{order.id} • {formatDate(order.createdAt)}
                          </div>
                          <div className="flex items-center mt-1 space-x-2">
                            <span className="text-xs text-muted-foreground">
                              {order.documentCount} document{order.documentCount !== 1 ? "s" : ""}
                            </span>
                            <span className="text-xs">•</span>
                            <span className="text-xs font-medium">
                              ${order.totalAmount.toFixed(2)}
                            </span>
                            <span className="text-xs">•</span>
                            <span>{getStatusBadge(order.status)}</span>
                          </div>
                        </div>
                      </div>
                      <Button asChild size="sm" variant="outline">
                        <Link to={`/buyer/orders/${order.id}`}>
                          View Details
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Link>
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <p className="text-muted-foreground">No orders found</p>
                </div>
              )}
            </CardContent>
            <CardFooter>
              <Button asChild variant="outline" className="w-full">
                <Link to="/buyer/orders">View All Orders</Link>
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </BuyerLayout>
  );
};

export default Dashboard;
